import { useSetToast } from "@app/components/Toast";
import { useUpdateObjective } from "@app/screens/Objectives/data";
import { Textfield } from "@design-system/Inputs/Textfield";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { GetObjectiveQuery, ResponseStatus } from "@generated/client/graphql";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { RESOURCE_NAME, keys } from "./i18n";
import { Box } from "@design-system/Layout/Box";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";

type NameInputProps = {
    objective: GetObjectiveQuery["objective"];
    editable?: boolean;
};

export const NameInput: FC<NameInputProps> = ({ objective, editable }) => {
    const [name, setName] = useState<string | undefined>(
        objective?.name || undefined,
    );
    const { mutate } = useUpdateObjective(objective?.id, objective?.entityId);

    const { setToastError } = useSetToast();
    const handleNameSave = () => {
        if (!editable) return;
        if (name !== objective.name) {
            const newName = name || null;
            mutate(
                { id: objective.id, name: newName },
                {
                    onSettled: (data) => {
                        if (
                            data?.updateObjective?.status ===
                            ResponseStatus.Error
                        ) {
                            setToastError(
                                `${data.updateObjective.error?.message}`,
                            );
                        }
                    },
                    onError: () => {
                        setToastError(t(keys.error_update));
                    },
                },
            );
        }
    };

    const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!editable) return;
        setName(event.target.value || undefined);
    };

    const { t } = useTranslation(RESOURCE_NAME);

    useEffect(() => {
        setName(objective?.name || undefined);
    }, [objective]);

    return (
        <FlexCol w="full" gap="2">
            <Text color="secondary">{t(keys.name_label)}</Text>
            <FlexRow w="full" gap="4" alignItems="center">
                <Box
                    w="full"
                    className={
                        !editable
                            ? "pointer-events-none cursor-not-allowed"
                            : ""
                    }
                >
                    <Textfield
                        value={name}
                        onInput={handleNameChange}
                        onBlur={handleNameSave}
                        placeholder={t(keys.name_placeholder)}
                        required
                    />
                </Box>
                <Tooltip>
                    <Tooltip.Trigger asChild>
                        <Icon
                            name="circleInfo"
                            size="sm"
                            className="text-secondary"
                        />
                    </Tooltip.Trigger>
                    <Tooltip.Content className="max-w-[400px]">
                        <Trans
                            t={t}
                            i18nKey={keys.hint_name}
                            components={{
                                ul: (
                                    <ul className="list-inside list-disc text-white text-small font-bold" />
                                ),
                                li: (
                                    <li
                                        key="key"
                                        className="pl-4 text-white text-small font-bold"
                                    />
                                ),
                                br: (
                                    <br
                                        key="br-1"
                                        className="content-[''] block my-2"
                                    />
                                ),
                            }}
                        />
                    </Tooltip.Content>
                </Tooltip>
            </FlexRow>
        </FlexCol>
    );
};
